import type React from 'react';
import { useEffect } from 'react';

import type { Text } from '@zillow/constellation';
import { AdornedInput, Adornment, FormField, FormHelp, Input, Label, Paragraph } from '@zillow/constellation';

import type { Section } from '../../../../models/index';
import { UTILITIES_PAYMENTS } from '../../../../constants';
import { stopPropagationIfInvalidNumber } from '../../../../utils/validatorTypes/PositiveIntegerValidator';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function UtilitiesPayments({ section, setSectionValues }: Props): JSX.Element {
  const isValid = (value: string): boolean => Number(value) >= UTILITIES_PAYMENTS.BOUNDS.MIN_DAYS;
  const amountHasError = section.getField(UTILITIES_PAYMENTS.FIELD_NAME.DAYS_AFTER_BILLING).hasError;

  useEffect(() => {
    section.setCustomFieldValidator(UTILITIES_PAYMENTS.FIELD_NAME.DAYS_AFTER_BILLING, isValid);
  }, []);

  function setDaysAfterBilling(event: React.FormEvent<typeof Text>): void {
    setSectionValues({
      [UTILITIES_PAYMENTS.FIELD_NAME.DAYS_AFTER_BILLING]: event.currentTarget.value,
    });
  }

  return (
    <>
      <Paragraph marginBottom="xs" fontType="h6">
        Utilities and services payment timing
      </Paragraph>
      <FormField>
        <Label id="variable-utility-costs">
          All variable utility costs will be billed monthly, and must be paid by tenant within
        </Label>
        <AdornedInput
          inputId="AdornedInput-utilities-and-services-payment"
          rightAdornment={<Adornment>days after billing</Adornment>}
          input={
            <Input
              aria-labelledby="variable-utility-costs"
              data-test="utilities-and-services-payment"
              type="number"
              onKeyDown={stopPropagationIfInvalidNumber}
              value={section.getFieldValue(UTILITIES_PAYMENTS.FIELD_NAME.DAYS_AFTER_BILLING)}
              onChange={setDaysAfterBilling}
            />
          }
          error={amountHasError}
        />
        <FormHelp>{`15 days is most common`}</FormHelp>
        {amountHasError && (
          <FormHelp error>{`Enter a number higher than ${UTILITIES_PAYMENTS.BOUNDS.MIN_DAYS} days`}</FormHelp>
        )}
      </FormField>
    </>
  );
}
